import { React, useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { GoogleMap, useLoadScript, Marker, MarkerF, InfoWindowF } from "@react-google-maps/api";
import "./globals.css";
import { api, publicCatalogRequest } from "../../utils/api";
import { getMapDefaultCenter } from "../../utils/p1Region";
import { Box } from "@mui/material";
import SearchFilterBar from "./SearchFilterBar";
import MerchantInfoWindow from "./MerchantInfoWindow";
import customMarkerIcon from './icon-washclub.png';
import {
  accessUsersLocation,
  getAllAvailableProducts,
  getMerchantStoreAccountNames,
  getMerchantStoresWithProducts,
  getMerchantsWithCoordinates,
  groupMerchantStoresByAccount,
  matchMerchantProducts,
  matchSearchToMerchant
} from "./utils";

const DEFAULT_MAP_CENTER = getMapDefaultCenter();

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_PUBLIC_GOOGLE_MAPS_API_KEY;

const MissingMapKeyMessage = () => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 p-6 bg-gray-50 text-center rounded-2xl">
    <h2 className="text-xl font-semibold text-gray-800">Google Maps API key is missing</h2>
    <p className="text-gray-600 max-w-md">
      Add <code className="bg-gray-200 px-1 rounded">REACT_APP_PUBLIC_GOOGLE_MAPS_API_KEY</code> to <code className="bg-gray-200 px-1 rounded">.env.local</code> (see <code className="bg-gray-200 px-1 rounded">.env.example</code>).
    </p>
  </div>
);

const MerchantStoreMapInner = () => {
  const [searchParams] = useSearchParams();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);

  const [zoom, setZoom] = useState(11);
  const [center, setCenter] = useState(DEFAULT_MAP_CENTER);
  const [openStore, setOpenStore] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [map, setMap] = useState(null);
  const hasPannedFromUrl = useRef(false);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);
  const onMapUnmount = useCallback(() => setMap(null), []);

  const toggleOpen = (id) => (openStore === id ? setOpenStore("") : setOpenStore(id));
  const closeStore = () => setOpenStore("");

  /** Not used for now. We'd like to group merchant stores by merchant account by customising the Marker on the 
      map, e.g. all merchant stores owned by Merchant X will have a green color, and so on. */
  const merchantStoresByAccount = merchants && groupMerchantStoresByAccount(merchants);

  const allProducts = useMemo(() => getAllAvailableProducts(merchants), [merchants]);

  useEffect(() => {
    accessUsersLocation(accessUsersLocationSuccess, accessUsersLocationError);

    const getMerchantStores = async () => {
      const response = await api("v1/merchantStore", "GET", null, publicCatalogRequest);
      if (response.data.success) {
        const merchantsWithCoordinates = await getMerchantsWithCoordinates(response.data.data.allMerchantStores);
        const merchantsWithAccountName = await getMerchantStoreAccountNames(merchantsWithCoordinates);
        const merchantsWithProducts = await getMerchantStoresWithProducts(merchantsWithAccountName);
        setMerchants(merchantsWithProducts);
        setFilteredMerchants(merchantsWithProducts);
      }
    };
    getMerchantStores();
  }, []);

  const accessUsersLocationSuccess = (position) => {
    const { latitude, longitude } = position.coords;
    setCenter({ lat: latitude, lng: longitude });
  };

  const accessUsersLocationError = () => {
    console.log("Unable to retrieve your location");
  };

  useEffect(() => {
    if (!searchValue && filteredProducts.length === 0) {
      setFilteredMerchants(merchants);
    } else if (!!searchValue && filteredProducts.length === 0) {
      setFilteredMerchants(merchants.filter((merchant) => matchSearchToMerchant(searchValue, merchant)));
    } else if (filteredProducts.length > 0 && !searchValue) {
      setFilteredMerchants(merchants.filter((merchant) => matchMerchantProducts(merchant, filteredProducts)));
    } else {
      setFilteredMerchants(
        merchants.filter(
          (merchant) =>
            matchMerchantProducts(merchant, filteredProducts) && matchSearchToMerchant(searchValue, merchant)
        )
      );
    }
  }, [filteredProducts, searchValue]);

  // On load with ?address= from "View Location", pan map to that location (once)
  useEffect(() => {
    if (!map || !isLoaded || !window.google || hasPannedFromUrl.current) return;
    const address = searchParams.get("address");
    if (!address) return;
    hasPannedFromUrl.current = true;
    setSearchValue(address);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address },
      (results, status) => {
        if (status !== "OK" || !results?.[0]) return;
        const result = results[0];
        if (result.geometry?.viewport) {
          map.fitBounds(result.geometry.viewport);
          const z = map.getZoom();
          if (z != null) map.setZoom(Math.max(1, z - 1));
        } else if (result.geometry?.location) {
          map.panTo(result.geometry.location);
          map.setZoom(11);
        }
      }
    );
  }, [map, isLoaded, searchParams]);

  const clearFilters = () => setFilteredProducts([]);

  return !isLoaded ? (
    <div>Loading...</div>
  ) : (
    <div className="m-2 rounded-2xl overflow-hidden">
      {/* <Box sx={{ marginTop: "12px", marginBottom: "18px" }}>
      </Box> */}
      <SearchFilterBar
        products={allProducts}
        filteredProducts={filteredProducts}
        setFilteredProducts={setFilteredProducts}
        setSearchValue={setSearchValue}
        clearFilters={clearFilters}
      />
      <GoogleMap zoom={zoom} center={center} onLoad={onMapLoad} onUnmount={onMapUnmount} mapContainerClassName="map-container">
        <Marker position={center} />
        {filteredMerchants &&
          filteredMerchants.map((merchant) => (
            <MarkerF
              key={`merchant-marker-f-${merchant._id}`}
              position={{ lat: merchant.coordinates.lat, lng: merchant.coordinates.lng }}
              icon={{ url: customMarkerIcon, scaledSize: new window.google.maps.Size(40, 40) }}
              onClick={() => {
                toggleOpen(merchant._id);
              }}
            >
              {openStore === merchant._id && (
                <InfoWindowF onCloseClick={closeStore}>
                  <MerchantInfoWindow merchant={merchant} key={`merchant-info-window-${merchant._id}`} />
                </InfoWindowF>
              )}
            </MarkerF>
          ))}
      </GoogleMap>
    </div>
  );
};

const MerchantStoreMap = () => {
  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY.trim() === "") {
    return <MissingMapKeyMessage />;
  }
  return <MerchantStoreMapInner />;
};

export default MerchantStoreMap;
